<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2026 (original work) Open Assessment Technologies SA
    import { TabGroup, DataTable } from '@oat-sa-private/ui-components';
    import CheckCell from '@/component/ui-elements/checkCell/CheckCell.svelte';
    import IconButtonCell from '@/component/ui-elements/iconButtonCell/IconButtonCell.svelte';
    import CandidateNameCell from '@/component/ui-elements/candidateNameCell/CandidateNameCell.svelte';
    import { __, getLanguageName } from '@oat-sa-private/ui-core';
    import RibbonContainer from '@/component/ui-elements/ribbonContainer/RibbonContainer.svelte';
    import NotificationManager from '@/component/ui-elements/notificationManager/NotificationManager.svelte';
    import { interactions, selectedInteractions } from '@/store/interactions';
    import { activeTab as activeTabStore, activeGroup as activeGroupStore } from '@/store/navigation';
    import { onMount, tick } from 'svelte';
    import { Dropdown } from '@oat-sa-private/ui-elements';
    import { sortInteractionsBy, timestampToFinishTime } from '@/core/utils/parseInteractionsArray';
    import { initWebSocket, closeWebSocket } from '@/core/webSocket/webSocketHandler';
    import router from '@/core/router/router';
    import {
        tabKeys,
        tabKeysEnableToHide,
        deliveryExecutionStatuses,
        routes,
        ltiClaims,
        recoverableErrors as errorKeys,
        actionsList,
        actionsIcons,
        timerLevels
    } from '@/core/utils/consts';
    import getInteractionUserName from '@/core/utils/getInteractionUserName';
    import { getDxStatusLabel, interactionsToAcs } from '@/core/utils/interactions.js';
    import { decodedAccessToken } from '@/store/accessToken';
    import { recoverableErrors } from '@/store/recoverableErrors';
    import recoverableErrorsManager from '@/core/recoverableErrorsManager/recoverableErrorsManager';
    import SelectBarContainer from '@/component/monitoring/SelectBarContainer.svelte';
    import DurationCell from '@/component/ui-elements/durationCell/DurationCell.svelte';
    import TextCell from '@/component/ui-elements/textCell/TextCell.svelte';
    import PlaceholderProgressBarCell from '@/component/ui-elements/placeholderProgressBarCell/PlaceholderProgressBarCell.svelte';
    import { onGoingAcs, rejectedAcs } from '@/store/acs';
    import getAvailableActions, { getIsResumeEnabled } from '@/core/utils/getAvailableActions';
    import { postBulkAcs } from '@/core/acs/acs.js';
    import { postAuthorize } from '@/core/api/api.js';
    import env from '@/config/env';
    import * as Types from '../../core/model/types.js'; // eslint-disable-line no-unused-vars
    import actionToRouteKey from '@/core/utils/actionToRouteKey';
    import { configurationStore } from '@/store/tenantConfiguration';
    import { getShowIPAddress, getHideHeader } from '@/core/utils/ltiClaims';

    export let initialSortBy = '';

    let selectMode = false;

    let tabs = [];

    let acsEnabled = false;

    const rows = {
        notStarted: [],
        awaitingAuthorization: [],
        inProgress: [],
        paused: [],
        finished: []
    };

    const resetLabel = __('reset');
    const getInitialHeaders = () => ({
        notStarted: [
            { key: 'user.name', cellComponent: CandidateNameCell, title: __('Name') },
            { key: 'title', cellComponent: TextCell, title: __('Battery / Delivery') }
        ],
        awaitingAuthorization: [
            { key: 'checkbox', title: '', cellComponent: CheckCell },
            { key: 'user.name', cellComponent: CandidateNameCell, title: __('Name'), sortable: true },
            { key: 'title', cellComponent: TextCell, title: __('Battery / Delivery'), sortable: true },
            { key: 'status', title: __('Status'), sortable: true },
            { key: 'ipAddress', title: __('IP address'), sortable: true },
            { key: 'authorize', cellComponent: IconButtonCell, fitContent: true }
        ],
        inProgress: [
            { key: 'checkbox', title: '', cellComponent: CheckCell },
            { key: 'user.name', cellComponent: CandidateNameCell, title: __('Name'), sortable: true },
            { key: 'title', cellComponent: TextCell, title: __('Battery / Delivery'), sortable: true },
            { key: 'ipAddress', title: __('IP address'), sortable: true },
            {
                key: 'progressPercentage',
                title: __('Completion'),
                cellComponent: PlaceholderProgressBarCell,
                sortable: true,
                icon: $recoverableErrors.internetConnection.status || $recoverableErrors.ws.status ? 'warning-16' : ''
            },
            {
                key: 'positionLabel',
                title: __('Position'),
                sortable: true,
                icon: $recoverableErrors.internetConnection.status || $recoverableErrors.ws.status ? 'warning-16' : ''
            },
            {
                key: 'startTs',
                title: __('Time spent'),
                cellComponent: DurationCell,
                sortable: true
            },
            {
                key: 'remainingTime',
                title: __('Time remaining'),
                cellComponent: DurationCell,
                sortable: false,
                icon: $recoverableErrors.internetConnection.status || $recoverableErrors.ws.status ? 'warning-16' : ''
            },
            {
                key: 'additionalTime',
                title: __('Additional time'),
                cellComponent: DurationCell,
                sortable: true
            }
        ],
        paused: [
            { key: 'checkbox', title: '', cellComponent: CheckCell },
            { key: 'user.name', cellComponent: CandidateNameCell, title: __('Name'), sortable: true },
            { key: 'title', cellComponent: TextCell, title: __('Battery / Delivery'), sortable: true },
            { key: 'ipAddress', title: __('IP address'), sortable: true },
            {
                key: 'additionalTime',
                title: __('Additional time'),
                cellComponent: DurationCell,
                sortable: true
            },
            { key: 'resume', cellComponent: IconButtonCell, fitContent: true }
        ],
        finished: [
            { key: 'checkbox', title: '', cellComponent: CheckCell },
            { key: 'user.name', cellComponent: CandidateNameCell, title: __('Name'), sortable: true },
            { key: 'title', cellComponent: TextCell, title: __('Battery / Delivery'), sortable: true },
            { key: 'ipAddress', title: __('IP address'), sortable: true },
            { key: 'status', title: __('Cause'), sortable: true },
            { key: 'endTs', title: __('Finish time'), sortable: true },
            {
                key: 'startTs',
                title: __('Time spent'),
                cellComponent: DurationCell,
                sortable: true
            }
        ]
    });
    let headers = getInitialHeaders();

    let selectBarContainerInstance = null;

    const isResumeEnabled = getIsResumeEnabled($decodedAccessToken);
    const showIPColumn = getShowIPAddress($decodedAccessToken);
    $: acsEnabled = getAvailableActions($activeTabStore, $interactions).length > 0;
    $: showRemainingTimeColumn = getShowTimeRemainingColumn($interactions);
    $: showTimeSpentColumn = getShowTimeSpentColumn($interactions);

    $: updateColumns(showRemainingTimeColumn, showTimeSpentColumn, showIPColumn, isResumeEnabled, acsEnabled);

    $: updateHeaderIcons($recoverableErrors);

    $: updateRows(filteredInteractions, !uiControl.clickableRows, $onGoingAcs, $rejectedAcs, selectMode, headers, $configurationStore);

    $: updateTabs($recoverableErrors.nrps.status, $recoverableErrors.interactions.status, {
        notStarted: rows.notStarted.length,
        awaitingAuthorization: rows.awaitingAuthorization.length,
        inProgress: rows.inProgress.length,
        paused: rows.paused.length,
        finished: rows.finished.length
    });

    /** @type {Types.Interaction[]} */
    $: filteredInteractions = joinSequenceBatteries(
        $interactions.reduce(
            (accumulator, currentInteraction) => {
                const status = currentInteraction?.deliveryExecution?.status;
                let category;

                if (currentInteraction?.isNotStartedUser) {
                    category = 'notStarted';
                } else if (
                    !status ||
                    [deliveryExecutionStatuses.initial, deliveryExecutionStatuses.authorized].includes(status)
                ) {
                    category = 'awaitingAuthorization';
                } else if (
                    status === deliveryExecutionStatuses.interacting ||
                    status === deliveryExecutionStatuses.deleted
                ) {
                    category = 'inProgress';
                } else if (status === deliveryExecutionStatuses.suspended) {
                    category = 'paused';
                } else if ([deliveryExecutionStatuses.terminated, deliveryExecutionStatuses.closed].includes(status)) {
                    category = 'finished';
                }

                if (category) {
                    accumulator[category].push(currentInteraction);

                    // Handle battery ID for non-finished statuses
                    if (category !== 'finished' && currentInteraction.battery?.id) {
                        accumulator.batteries.push(`${currentInteraction.battery.id}${currentInteraction.user.id}`);
                    }
                }

                return accumulator;
            },
            {
                notStarted: [],
                awaitingAuthorization: [],
                inProgress: [],
                paused: [],
                finished: [],
                batteries: []
            }
        )
    );

    $: uiControl = {
        clickableRows:
            acsEnabled &&
            $recoverableErrors.internetConnection.status === false &&
            $recoverableErrors.nrps.status === false,
        displayNotStartedDataTable: $recoverableErrors.nrps.status === false,
        disableDropdown:
            $recoverableErrors.internetConnection.status ||
            $recoverableErrors.ws.retrying ||
            $recoverableErrors.nrps.status
    };

    let title = '';
    let groups = getGroups();

    /**
     * Initial sort of interactions array & which columns are displayed
     */
    onMount(async () => {
        setActiveGroup();
        title = getActiveGroupLabel();
        await tick();
        if (initialSortBy) {
            $interactions = sortInteractionsBy($interactions, initialSortBy);
            applyTabDefaultSort($activeTabStore);
        }
        if (groups.length > 0) {
            recoverableErrorsManager.reset([errorKeys.websocket, errorKeys.nrps, errorKeys.interactions]);

            closeWebSocket();

            initWebSocket(env('WS_URL'), $activeGroupStore);
        }
    });

    /**
     * Update shown tab headers
     */
    function updateColumns() {
        headers = getInitialHeaders();
        let removeKeys = [];

        if (!showIPColumn) {
            removeKeys.push('ipAddress');
            for (const key in headers) {
                headers[key] = headers[key].filter(column => column.key !== 'ipAddress');
            }
        }
        if (!showRemainingTimeColumn) {
            removeKeys.push('remainingTime');
            removeKeys.push('additionalTime');
        }
        if (!showTimeSpentColumn) {
            removeKeys.push('startTs');
        }
        if (!isResumeEnabled) {
            removeKeys.push('resume');
        }
        if (!acsEnabled) {
            removeKeys.push('checkbox');
        }

        if (removeKeys.length) {
            for (const key in headers) {
                headers[key] = headers[key].filter(column => !removeKeys.includes(column.key));
            }
        }
    }

    /**
     * Set column with provided key sort order
     * @param {Object[]} headerColumns
     * @param {String} columnKey - key of the column
     * @param {'asc'|'desc'|undefined} sortOrder
     * @returns {Object[]} updated header columns
     */
    function updateHeaderSortOrder(headerColumns, columnKey, sortOrder) {
        return headerColumns.map(column => {
            delete column.sortOrder;
            if (column.key === columnKey) {
                column.sortOrder = sortOrder;
                column.lastSortOrder = sortOrder;
            }
            return column;
        });
    }

    /**
     * Remove sortOrder from header columns
     * @param {Object[]} headerColumns
     * @returns {Object[]} updated header columns
     */
    function resetHeadersSortOrder(headerColumns) {
        return headerColumns.map(column => {
            delete column.sortOrder;
            return column;
        });
    }

    /**
     * NOTE: NRPS functionality is disabled until fixes are made on backend.
     * After that, decide if this function is still useful for some cases - or remove it.
     *
     * Removes 'not Started' tab if NRPS link is not provided in the jwt token, either on
     * nrps claim or as a custom claim for nrps on extra groups
     * @returns {boolean} value indicating whethere to show or hide the not started tab
     */
    // eslint-disable-next-line no-unused-vars
    function showNrps() {
        const nrpsForMainContext = typeof $decodedAccessToken[ltiClaims.nrps] !== 'undefined';
        const nrpsForExtraGroups =
            $decodedAccessToken[ltiClaims.custom] &&
            typeof $decodedAccessToken[ltiClaims.custom]['groups.nrpsUrlPattern'] !== 'undefined';

        // if nrps is set for main context but not for extra contexst, only show tab if current context is main context
        if (
            nrpsForMainContext &&
            (!$activeGroupStore || $decodedAccessToken[ltiClaims.context].id === $activeGroupStore)
        ) {
            return true;
        }
        // if nrps is set for extra contexts but not for main context, only show tab if current context is not main context
        if (
            nrpsForExtraGroups &&
            $activeGroupStore &&
            $decodedAccessToken[ltiClaims.context].id !== $activeGroupStore
        ) {
            return true;
        }

        return false;
    }

    /**
     * Should show 'Time remaining'+'Additional time' columns only if some deliveries have timers
     * @returns {boolean} value indicating whether to show or hide the 'Time remaining'+'Additional time' columns
     */
    function getShowTimeRemainingColumn() {
        return $interactions.some(i => i.isTimerExists);
    }

    /**
     * Should show 'Time spent' column only if some deliveries do not have timers
     * @returns {boolean} value indicating whether to show or hide the 'Time spent' columns
     */
    function getShowTimeSpentColumn() {
        return $interactions.some(i => !i.isTimerExists);
    }

    /**
     * Sorts interactions based on a parameter and handles styles and sorting direction timeouts.
     * Notice that logic to handle asc/desc sorting is only handled when sorting is triggered
     * manually by user click on table headers
     * @param {String} sortBy - the key of the clicked column
     * @param {'asc'|'desc'} sortOrder - sort order
     */
    function sortInteractions(sortBy, sortOrder) {
        $interactions = sortInteractionsBy($interactions, sortBy);

        if (sortOrder === 'desc') {
            $interactions = $interactions.reverse();
        }
    }

    /**
     * on proctoring tab change, uncheck every selected row on current tab,
     * set clicked tab as the active one, save it on activeTabStore,
     * and reset ordering logic. Remove timeout handler if existed
     * Also, if selectMode is disabled - it'll enable it.
     * @param {Event} e - click event
     */
    const onTabChange = e => {
        rows[$activeTabStore].forEach(row => {
            if (row[0].actionType === 'check') {
                row[0].checked = false;
            }
        });
        if (selectMode) {
            selectMode = false;
        }

        selectBarContainerInstance.resetVisibility();

        $activeTabStore = e.detail.key;

        applyTabDefaultSort($activeTabStore);
    };

    /**
     * Tells if any row is disabled, based on its data.
     * Function passed as prop to Datatable.
     * @param {Object} row
     * @returns {Boolean}
     */
    const rowDisabled = row => row[0].warning;

    /**
     * Tells if any row is selected, based on its CheckCell data.
     * Function passed as prop to Datatable.
     * @param {Object} row
     * @returns {Boolean}
     */
    const rowSelected = row => row[0].checked;

    /**
     * Apply default sort for active tab
     * @param {String} currentActiveTab - active tab
     */
    function applyTabDefaultSort(currentActiveTab) {
        switch (currentActiveTab) {
            case tabKeys.awaitingAuthorization:
                sortInteractions('user.name');
                headers.awaitingAuthorization = updateHeaderSortOrder(
                    headers.awaitingAuthorization,
                    'user.name',
                    'asc'
                );
                break;

            case tabKeys.paused:
                sortInteractions('user.name');
                headers.paused = updateHeaderSortOrder(headers.paused, 'user.name', 'asc');
                break;

            case tabKeys.inProgress:
                sortInteractions('progressPercentage');
                headers.inProgress = updateHeaderSortOrder(headers.inProgress, 'progressPercentage', 'asc');
                break;

            case tabKeys.finished:
                sortInteractions('user.name');
                headers.finished = updateHeaderSortOrder(headers.finished, 'user.name', 'asc');
                break;
        }
    }

    /**
     * Get groups to be displayed on the group switcher. Available groups come from
     * context claim (default context) and custom groups.allContext claim
     * @returns {array} - array of groups, each group is an object with format {key: context id, label: label to display}
     */
    function getGroups() {
        let res = [];
        const defaultContext = getContext();
        const allContexts =
            $decodedAccessToken[ltiClaims.custom] && $decodedAccessToken[ltiClaims.custom]['groups.allContexts'];
        const showAllLabel =
            $decodedAccessToken[ltiClaims.custom] && $decodedAccessToken[ltiClaims.custom]['groups.allowDisplayAll'];
        const isDefaultInAll = allContexts?.filter(context => context.id === defaultContext.id)?.length > 0;

        if (allContexts) {
            if (showAllLabel) {
                allContexts.push({ id: 'all_contexts', label: 'All' });
            }

            !isDefaultInAll && allContexts.push(defaultContext);
            res = allContexts.map(context => ({ key: context.id, label: context.label || context.id }));
            res.sort((a, b) => {
                const labelA = a.label.toLowerCase();
                const labelB = b.label.toLowerCase();

                if (labelA < labelB) {
                    return -1;
                }
                if (labelA > labelB) {
                    return 1;
                }
                return 0;
            });
        }
        return res;
    }

    /**
     * Gets active group. If it is not set on activeGroup store,
     * gets default context on LTI launch
     * and gives it to active group
     */
    function setActiveGroup() {
        if (!$activeGroupStore) {
            const allContexts =
                $decodedAccessToken[ltiClaims.custom] && $decodedAccessToken[ltiClaims.custom]['groups.allContexts'];
            const showAllLabel =
                $decodedAccessToken[ltiClaims.custom] &&
                $decodedAccessToken[ltiClaims.custom]['groups.allowDisplayAll'];
            const defaultSelected =
                $decodedAccessToken[ltiClaims.custom] &&
                $decodedAccessToken[ltiClaims.custom]['groups.defaultSelected'];

            if (allContexts) {
                if (showAllLabel) {
                    $activeGroupStore = defaultSelected || 'all_contexts';
                } else {
                    $activeGroupStore = defaultSelected || getContext().id;
                }
            } else {
                $activeGroupStore = getContext().id;
            }
        }
    }

    function getContext() {
        return {
            id: $decodedAccessToken[ltiClaims.context].id || 'default_local',
            label: $decodedAccessToken[ltiClaims.context].label || ''
        };
    }

    function launchedFromPortal() {
        return $decodedAccessToken[ltiClaims.custom]?.portalLaunch || false;
    }

    function getActiveGroupLabel() {
        let activeGroupLabel = '';
        if (groups?.length > 0) {
            activeGroupLabel = groups.filter(ctx => ctx.key === $activeGroupStore)[0]?.label;
        } else {
            activeGroupLabel = getContext().label;
        }
        return activeGroupLabel;
    }

    function updateTitle(contextId) {
        const group = contextId && groups?.filter(ctx => ctx.key === contextId);
        const groupLabel = group && group[0]?.label;

        title = groupLabel || $decodedAccessToken[ltiClaims.context].label || '';
    }

    /**
     * Resets ws and nrps recoverable errors to its default value, updates
     * active group, and then requests interactions list for the context
     * of selected group. At the end, reset SelectBarContainer visibility
     * @param {Object} event - click event
     * @param {Object} event.detail - click event details
     * @param {String} event.detail.value - contextid attached to the clicked group
     */
    function handleGroupChange({ detail: { value: newGroup } }) {
        recoverableErrorsManager.reset([errorKeys.websocket, errorKeys.nrps, errorKeys.interactions]);

        closeWebSocket();

        activeGroupStore.set(newGroup);
        interactions.set([]);
        resetRows();
        applyTabDefaultSort($activeTabStore);
        updateTitle($activeGroupStore);

        initWebSocket(env('WS_URL'), $activeGroupStore);

        selectBarContainerInstance.resetVisibility();
    }

    /**
     * Update 'checked' property of row
     * @param {String} tabKey - key of the tab in which this row is presented
     * @param {Object} rowKey - index of the row that needs to be updated
     * @param {Boolean} [newCheckedValue] - if providev - 'checked' property
     * of the row will be updated to this value otherwise it'll be switched
     * from true to false and vice versa
     */
    function updateRowCheckedValue(tabKey, rowKey, newCheckedValue) {
        rows[tabKey] = rows[tabKey].map((row, index) => {
            if (index === rowKey && !row[0].disabled) {
                if (typeof newCheckedValue !== 'undefined') {
                    row[0].checked = newCheckedValue;
                } else {
                    row[0].checked = !row[0].checked;
                }
            }
            return row;
        });
    }

    /**
     * Updates row `checked` property and sets selectMode to true if
     * it wasn't enabled yet.
     * @param {object} event - check event received from DataTable
     * @param {object} event.key - Index of the clicked row
     * @param {object} event.checked - checkbox status
     */
    function handleCheck(event) {
        if (!selectMode) {
            selectMode = true;
        }
        updateRowCheckedValue($activeTabStore, event.key, event.checked);
    }

    /**
     * Handles 'resume' event from a table row in 'paused' tab
     * @param {object} eventDetail - detail of event received from DataTable
     * @param {object} eventDetail.resource - row data
     */
    function handleRowResume(eventDetail) {
        const row = eventDetail.resource;
        const cell = row.find(cellData => cellData.actionType === actionsList.resume);

        const checkedInteraction = filteredInteractions[$activeTabStore].find(
            interaction => interaction.deliveryExecution.id === cell.key
        );
        if (checkedInteraction) {
            // Send a single 'resume' ACS message
            // Notifications and iconButtonCell disabled state are handled by existing store reactivity
            postBulkAcs(actionsList.resume, [checkedInteraction]);
        }
    }

    /**
     * Handles 'authorize' event from a table row in 'notStarted' tab
     * @param {object} eventDetail - detail of event received from DataTable
     * @param {object} eventDetail.resource - row data
     */
    function handleRowAuthorize(eventDetail) {
        const row = eventDetail.resource;
        const cell = row.find(cellData => cellData.actionType === actionsList.authorize);

        const checkedInteraction = filteredInteractions[$activeTabStore].find(
            interaction => interaction.deliveryExecution.id === cell.key
        );
        if (checkedInteraction) {
            // Send a single 'authorize' message
            // Notifications and iconButtonCell disabled state are handled by existing store reactivity
            const checkedAsAcs = interactionsToAcs([checkedInteraction], actionsList.authorize);
            postAuthorize(checkedAsAcs);
        }
    }

    /**
     * Redirects to details view, stores clickedInteraction in
     * selectedInteractionsStore so can be recovered from there
     * @param {integer} clickedRowIndex  - clicked row index
     * @param {string} tabKey  - tabkey of clicked row (inProgress or finished)
     */
    function redirectToDetails(clickedRowIndex, tabKey) {
        const allowRedirect = interaction =>
            interaction &&
            interaction.deliveryExecution.status !== deliveryExecutionStatuses.deleted &&
            $onGoingAcs.filter(acs => interaction.deliveryExecution.id === acs.delivery_execution_id).length === 0 &&
            $rejectedAcs.filter(acs => interaction.deliveryExecution.id === acs.delivery_execution_id).length === 0;

        let clickedInteraction = null;

        if (tabKey === tabKeys.inProgress) {
            clickedInteraction = filteredInteractions.inProgress[clickedRowIndex];
        } else if (tabKey === tabKeys.paused) {
            clickedInteraction = filteredInteractions.paused[clickedRowIndex];
        } else if (tabKey === tabKeys.finished) {
            clickedInteraction = filteredInteractions.finished[clickedRowIndex];
        }

        if (allowRedirect(clickedInteraction)) {
            selectedInteractions.set([clickedInteraction]);
            router.redirect(routes.details);
        }
    }

    /**
     * Sets icon on table headers if there is some connectivity error
     * @param {Object} errorStatus - current RecoverableErrors store value
     */
    function updateHeaderIcons(errorStatus) {
        const isError = errorStatus.internetConnection.status || errorStatus.ws.status;
        const icon = isError ? 'warning-16' : '';

        headers.inProgress.find(header => header.key === 'progressPercentage').icon = icon;
        headers.inProgress.find(header => header.key === 'positionLabel').icon = icon;
        headers.inProgress = headers.inProgress;
    }

    /**
     * Updates table tabs counters and error status
     * @param {Boolean} nrpsError - if there is NRPS error (true) or not (false)
     * @param {Boolean} interactionsError - if there is redis error (true) or not (false)
     * @param {Object} rowsCounter - Object containing the number of rows for each tab
     */
    function updateTabs(nrpsError, interactionsError, rowsCounter) {
        const hideTabs = $decodedAccessToken[ltiClaims.custom]?.hideTabs || [];
        tabs = [
            {
                key: tabKeys.awaitingAuthorization,
                label: `${__('Awaiting Authorization')} (${nrpsError ? ' - ' : rowsCounter.awaitingAuthorization})`
            },
            {
                key: tabKeys.inProgress,
                label: `${__('In progress')} (${interactionsError ? '-' : rowsCounter.inProgress})`
            },
            {
                key: tabKeys.paused,
                label: `${__('Paused')} (${interactionsError ? '-' : rowsCounter.paused})`
            },
            {
                key: tabKeys.finished,
                label: `${__('Finished')} (${interactionsError ? '-' : rowsCounter.finished})`
            }
        ];

        if (launchedFromPortal()) {
            tabs.unshift({
                key: tabKeys.notStarted,
                label: `${__('Not started')} (${interactionsError ? '-' : rowsCounter.notStarted})`
            });
        }

        tabs = tabs.filter(tab => !hideTabs.includes(tabKeysEnableToHide[tab.key]));
    }

    /**
     * Updates monitoring rows with latest interactions store value and
     * sets checkbox cell status
     * @param {Object} currentInteractions - current interactions store value
     * @param {Boolean} disableCheckbox - If checkbox cells must be disabled on each row (true) or not (false)
     * @param {Array} onGoingAcsStoreValue - Array with the requested ACS, so checkbox for those rows is displayed as a spinner
     * @param {Array} rejectedAcsStoreValue - Array with the rejected ACS, so checkbox for those rows is displayed as a warning icon
     */
    function updateRows(currentInteractions, disableCheckbox, onGoingAcsStoreValue, rejectedAcsStoreValue) {
        /*
         * Mapper to get an array of stringified row cells.
         * check cells are ignored and for progressCompletion cell
         * we get cell value instead of cell data
         */
        const mapData = row =>
            JSON.stringify(row.filter(cell => cell.actionType !== 'check').map(cell => cell.data || cell));

        // save stringified rows before updating them
        const oldRows = {
            awaitingAuthorization: rows.awaitingAuthorization.map(mapData) ?? [],
            inProgress: rows.inProgress.map(mapData),
            paused: rows.paused.map(mapData),
            finished: rows.finished.map(mapData)
        };
        const getInteractionTitle = interaction => {
            const batteryName = interaction.battery?.name;
            const interactionTitle = interaction.title;
            if (batteryName && interactionTitle?.length > 1) {
                return `${batteryName} (${interactionTitle})`;
            } else {
                return interactionTitle || '';
            }
        };
        const getCandidateNameCellData = interaction => ({
            name: getInteractionUserName(interaction.user),
            span: interaction.user.sourcedId || interaction.user.login || '',
            flagLanguageAccommodation: Boolean(interaction.flagLanguageAccommodation)
        });
        // set rows
        rows.awaitingAuthorization = currentInteractions.awaitingAuthorization.map(interaction => [
            {
                checked: (function () {
                    if (disableCheckbox) {
                        return false;
                    }
                    if (interaction?.deliveryExecution?.status === deliveryExecutionStatuses.authorized) {
                        return false;
                    }
                    const interactionRow = rows.awaitingAuthorization.find(
                        row => row[0].key === interaction?.deliveryExecution?.id
                    );
                    if (interactionRow) {
                        return interactionRow[0].checked;
                    }
                    return false;
                })(),
                actionType: 'check',
                disabled:
                    disableCheckbox || interaction?.deliveryExecution?.status === deliveryExecutionStatuses.authorized,
                key: interaction?.deliveryExecution?.id, // will be use to collect selected interactions on bulkAcs event
                spinner: Boolean(
                    onGoingAcsStoreValue.find(acs => acs.delivery_execution_id === interaction?.deliveryExecution?.id)
                ),
                warning: Boolean(
                    rejectedAcsStoreValue.find(acs => acs.delivery_execution_id === interaction?.deliveryExecution?.id)
                ),
                removableColumnKey: 'checkbox'
            },
            getCandidateNameCellData(interaction),
            {
                data: getInteractionTitle(interaction),
                span: interaction.locale && (getLanguageName(interaction.locale) || interaction.locale)
            },
            { data: getDxStatusLabel(interaction.deliveryExecution) },
            { data: interaction.ipAddress || '', removableColumnKey: 'ipAddress' },
            {
                // iconButtonCell props
                key: interaction?.deliveryExecution?.id,
                actionType: actionsList.authorize,
                icon: `${actionsIcons[actionsList.authorize]}-16`,
                title: __('Authorize'),
                hidden: selectMode || interaction?.deliveryExecution?.status === deliveryExecutionStatuses.authorized,
                disabled: Boolean(
                    onGoingAcsStoreValue.find(acs => acs.delivery_execution_id === interaction?.deliveryExecution?.id)
                )
            }
        ]);

        rows.inProgress = currentInteractions.inProgress.map(interaction => [
            {
                checked: (function () {
                    if (disableCheckbox) {
                        return false;
                    }
                    const interactionRow = rows.inProgress.find(row => row[0].key === interaction.deliveryExecution.id);
                    if (interactionRow) {
                        return interactionRow[0].checked;
                    }
                    return false;
                })(),
                actionType: 'check',
                disabled: disableCheckbox || interaction.deliveryExecution.status === deliveryExecutionStatuses.deleted,
                key: interaction.deliveryExecution.id, // will be use to collect selected interactions on bulkAcs event
                spinner:
                    typeof onGoingAcsStoreValue.find(
                        acs =>
                            acs.action !== actionsList.reopen &&
                            acs.delivery_execution_id === interaction.deliveryExecution.id
                    ) !== 'undefined',
                warning:
                    typeof rejectedAcsStoreValue.find(
                        acs => acs.delivery_execution_id === interaction.deliveryExecution.id
                    ) !== 'undefined',
                removableColumnKey: 'checkbox'
            },
            getCandidateNameCellData(interaction),
            {
                data: getInteractionTitle(interaction),
                span: interaction.locale && (getLanguageName(interaction.locale) || interaction.locale)
            },
            { data: interaction.ipAddress || '', removableColumnKey: 'ipAddress' },
            {
                data: interaction.progressPercentage,
                placeholder:
                    interaction.deliveryExecution.status === deliveryExecutionStatuses.deleted ? resetLabel : false
            },
            {
                data: interaction.positionLabel.toLowerCase().replace('#', ''),
                label: interaction.positionLabelSr
            },
            {
                startTs: interaction.deliveryExecution.startTs,
                endTs: interaction.deliveryExecution.endTs,
                removableColumnKey: 'startTs'
            },
            /* eslint-disable indent */
            Object.assign(
                { removableColumnKey: 'remainingTime' },
                interaction.currentTimerLevel
                    ? {
                          startTs: 0,
                          endTs: interaction.timers[interaction.currentTimerLevel].maxTimeRemaining,
                          extra:
                              interaction.timers[timerLevels.extra]?.maxTime > 0
                                  ? interaction.timers[timerLevels.extra]?.maxTimeRemaining
                                  : null,
                          timerLevel: interaction.currentTimerLevel
                      }
                    : {}
            ),
            Object.assign(
                { removableColumnKey: 'additionalTime' },
                interaction.timers && interaction.timers[timerLevels.extra]?.maxTime > 0
                    ? { startTs: 0, endTs: interaction.timers[timerLevels.extra].maxTime }
                    : {}
            )
            /*  eslint-enable indent */
        ]);

        rows.paused = currentInteractions.paused.map(interaction => [
            {
                checked: (function () {
                    if (disableCheckbox) {
                        return false;
                    }
                    const interactionRow = rows.paused.find(row => row[0].key === interaction.deliveryExecution.id);
                    if (interactionRow) {
                        return interactionRow[0].checked;
                    }
                    return false;
                })(),
                actionType: 'check',
                disabled: disableCheckbox,
                key: interaction.deliveryExecution.id, // will be use to collect selected interactions on bulkAcs event
                spinner: Boolean(
                    onGoingAcsStoreValue.find(acs => acs.delivery_execution_id === interaction.deliveryExecution.id)
                ),
                warning: Boolean(
                    rejectedAcsStoreValue.find(acs => acs.delivery_execution_id === interaction.deliveryExecution.id)
                ),
                removableColumnKey: 'checkbox'
            },
            getCandidateNameCellData(interaction),
            {
                data: getInteractionTitle(interaction),
                span: interaction.locale && (getLanguageName(interaction.locale) || interaction.locale)
            },
            { data: interaction.ipAddress || '', removableColumnKey: 'ipAddress' },
            Object.assign(
                { removableColumnKey: 'additionalTime' },
                interaction.timers && interaction.timers[timerLevels.extra]?.maxTime > 0
                    ? { startTs: 0, endTs: interaction.timers[timerLevels.extra].maxTime }
                    : {}
            ),
            {
                // iconButtonCell props
                key: interaction.deliveryExecution.id,
                actionType: actionsList.resume,
                icon: `${actionsIcons[actionsList.resume]}-16`,
                title: __('Resume'),
                hidden: selectMode,
                disabled: Boolean(
                    onGoingAcsStoreValue.find(acs => acs.delivery_execution_id === interaction.deliveryExecution.id)
                ),
                removableColumnKey: 'resume'
            }
        ]);
        rows.notStarted = currentInteractions.notStarted.map(notStartedUser => [
            getCandidateNameCellData(notStartedUser),
            {
                data: notStartedUser.title || ''
            }
        ]);
        const getFinishedInteractionTitle = interaction => {
            const batteryName = interaction.battery?.name;
            const interactionTitle = interaction.title;
            if (batteryName && interactionTitle?.length > 1) {
                return batteryName;
            } else {
                return interactionTitle || '';
            }
        };
        rows.finished = currentInteractions.finished.map(interaction => [
            {
                checked: (function () {
                    if (disableCheckbox) {
                        return false;
                    }
                    const interactionRow = rows.finished.find(row => row[0].key === interaction.deliveryExecution.id);
                    if (interactionRow) {
                        return interactionRow[0].checked;
                    }
                    return false;
                })(),
                actionType: 'check',
                disabled: disableCheckbox,
                key: interaction.deliveryExecution.id, // will be use to collect selected interactions on bulkAcs event
                spinner:
                    typeof onGoingAcsStoreValue.find(
                        acs =>
                            acs.action !== actionsList.terminate &&
                            acs.delivery_execution_id === interaction.deliveryExecution.id
                    ) !== 'undefined',
                warning:
                    typeof rejectedAcsStoreValue.find(
                        acs => acs.delivery_execution_id === interaction.deliveryExecution.id
                    ) !== 'undefined',
                removableColumnKey: 'checkbox'
            },
            getCandidateNameCellData(interaction),
            {
                data: getFinishedInteractionTitle(interaction),
                span: interaction.locale && (getLanguageName(interaction.locale) || interaction.locale)
            },
            { data: interaction.ipAddress || '', removableColumnKey: 'ipAddress' },
            { data: getDxStatusLabel(interaction.deliveryExecution) },
            { data: timestampToFinishTime(interaction.deliveryExecution.endTs) },
            { startTs: interaction.deliveryExecution.startTs, endTs: interaction.deliveryExecution.endTs }
        ]);

        for (const tabKey of Object.keys(headers)) {
            const columnKeys = headers[tabKey].map(column => column.key);
            rows[tabKey] = rows[tabKey].map(row =>
                row.filter(cell => !cell.removableColumnKey || columnKeys.includes(cell.removableColumnKey))
            );
        }

        // get stringified versions of new rows
        const newRows = {
            awaitingAuthorization: rows.awaitingAuthorization.map(mapData),
            inProgress: rows.inProgress.map(mapData),
            paused: rows.paused.map(mapData),
            finished: rows.finished.map(mapData)
        };

        // reset header sort state if corresponding rows were changed
        [tabKeys.awaitingAuthorization, tabKeys.inProgress, tabKeys.paused, tabKeys.finished].forEach(key => {
            if (checkRowsDiff(newRows[key], oldRows[key]) && oldRows[key].length) {
                headers[key] = resetHeadersSortOrder(headers[key]);
            }
        });
    }

    /**
     * Checks if two rows of stringified cells have different
     * lengths or different cell values. It does not
     * check cells order.
     * @param {Array} rowsA - Array of rows
     * @param {Array} rowsB - Array of rows
     * @returns {Boolean} - true if rowsA and rowsB have any difference (except regarding sorting)
     */
    function checkRowsDiff(rowsA, rowsB) {
        if (rowsA.length !== rowsB.length) {
            return true;
        }
        const joinedRows = [...rowsA, ...rowsB];
        return joinedRows.filter((row, i) => joinedRows.indexOf(row) === i).length !== rowsA.length;
    }

    /**
     * Reset rows to original (empty state)
     */
    function resetRows() {
        for (const tab in rows) {
            rows[tab] = [];
        }
    }

    /**
     * Set interactions from checked rows as selectedInteractions
     * and redirects to clicked action
     * @param {Object} params - event detail from row
     * @param {String} [params.acs] - clicked action. Must be a value from routes or actionsList consts
     * @param {Boolean} [params.useForm] - if true, handle bulkAcs by redirecting to its form page;
     *                                     if false, make the request now, without confirmation
     */
    function bulkAcs({ acs, useForm = true }) {
        const checkedDxIds = rows[$activeTabStore].filter(row => row[0].checked).map(row => row[0].key);
        const checkedInteractions = filteredInteractions[$activeTabStore].filter(
            interaction => interaction.deliveryExecution && checkedDxIds.includes(interaction.deliveryExecution.id)
        );
        const checkedWhereActionEnabled = checkedInteractions.filter(interaction =>
            getAvailableActions($activeTabStore, [interaction]).includes(acs)
        );
        if (checkedWhereActionEnabled.length) {
            const routeKeyAcs = actionToRouteKey(acs);
            if (useForm) {
                selectedInteractions.set(checkedWhereActionEnabled);
                router.redirect(routes[routeKeyAcs]);
            } else {
                postBulkAcs(actionsList[routeKeyAcs], checkedWhereActionEnabled);
            }
        }
    }

    /**
     * Creates row click handler
     * If selectMode is enabled - it'll update 'checked' value of the row
     * otherwise it'll redirect to details
     * @param {String} tabKey - key of the tab in which this row is presented
     * @returns {Function}
     */
    function createRowClickHandler(tabKey) {
        return evt => {
            if (selectMode) {
                updateRowCheckedValue(tabKey, evt.detail.key);
            } else {
                redirectToDetails(evt.detail.key, tabKey);
            }
        };
    }

    function backToSessionOverview() {
        window.parent.postMessage(
            {
                type: 'back-to-session-overview',
                payload: {
                    contextId: getContext().id
                }
            },
            '*'
        );
    }

    /**
     * Creates row long click/tap handler
     * If selectMode is disabled - it'll enable it.
     * Also it'll update row 'checked' property
     * @param {String} tabKey - key of the tab in which this row is presented
     * @returns {Function}
     */
    function createRowLongClickHandler(tabKey) {
        return evt => {
            if (!selectMode) {
                selectMode = true;
            }
            updateRowCheckedValue(tabKey, evt.detail.key);
        };
    }

    function toTimestampMs(timestamp) {
        if (timestamp === null || typeof timestamp === 'undefined') {
            return Number.NaN;
        }
        if (typeof timestamp === 'number') {
            return timestamp;
        }
        if (typeof timestamp === 'string') {
            const trimmed = timestamp.trim();
            if (!trimmed) {
                return Number.NaN;
            }
            const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
            const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
            const ms = Date.parse(normalized);
            return Number.isNaN(ms) ? Number.NaN : ms;
        }
        const ms = Date.parse(timestamp);
        return Number.isNaN(ms) ? Number.NaN : ms;
    }

    function joinSequenceBatteries(currentInteractions) {
        const { finished, batteries } = currentInteractions;

        const interactionsFilterByBattery = finished.reduce((accumulator, currentInteraction) => {
            if (currentInteraction.battery?.id) {
                // if there is battery session still pending, hide battery on Finished tab
                if (batteries.includes(`${currentInteraction.battery?.id}${currentInteraction.user?.id}`)) {
                    return accumulator;
                }

                const currentInteractionStartTs = toTimestampMs(currentInteraction?.deliveryExecution?.startTs);
                const currentInteractionEndTs = toTimestampMs(currentInteraction?.deliveryExecution?.endTs);
                const batteryInteractionStartTs = toTimestampMs(
                    accumulator[`${currentInteraction.battery.id}${currentInteraction.user.id}`]?.deliveryExecution
                        ?.startTs
                );
                const batteryInteractionEndTs = toTimestampMs(
                    accumulator[`${currentInteraction.battery.id}${currentInteraction.user.id}`]?.deliveryExecution
                        ?.endTs
                );

                if (accumulator[`${currentInteraction.battery.id}${currentInteraction.user.id}`]) {
                    // update sessions Time spend on battery cases
                    if (
                        !Number.isNaN(currentInteractionStartTs) &&
                        (Number.isNaN(batteryInteractionStartTs) ||
                            currentInteractionStartTs < batteryInteractionStartTs)
                    ) {
                        accumulator[
                            `${currentInteraction.battery.id}${currentInteraction.user.id}`
                        ].deliveryExecution.startTs = currentInteraction?.deliveryExecution?.startTs;
                    }

                    if (
                        !Number.isNaN(currentInteractionEndTs) &&
                        (Number.isNaN(batteryInteractionEndTs) || currentInteractionEndTs > batteryInteractionEndTs)
                    ) {
                        accumulator[`${currentInteraction.battery.id}${currentInteraction.user.id}`] = {
                            ...currentInteraction,
                            batteryName: currentInteraction.battery.name
                        };
                    }
                } else {
                    accumulator[`${currentInteraction.battery.id}${currentInteraction.user.id}`] = {
                        ...currentInteraction,
                        batteryName: currentInteraction.battery.name
                    };
                }
            } else {
                accumulator[currentInteraction.deliveryExecution.id] = currentInteraction;
            }
            return accumulator;
        }, []);

        if (Object.values(interactionsFilterByBattery).length === 0) {
            return { ...currentInteractions, finished: interactionsFilterByBattery };
        }

        // revert the original array order to keep sorts
        let revertArrayOrder = [];
        finished.forEach(interaction => {
            if (
                interaction.battery?.id &&
                interactionsFilterByBattery[`${interaction.battery.id}${interaction.user.id}`]
            ) {
                revertArrayOrder.push(interactionsFilterByBattery[`${interaction.battery.id}${interaction.user.id}`]);
                delete interactionsFilterByBattery[`${interaction.battery.id}${interaction.user.id}`];
            } else if (interactionsFilterByBattery[interaction.deliveryExecution.id]) {
                revertArrayOrder.push(interactionsFilterByBattery[interaction.deliveryExecution.id]);
                delete interactionsFilterByBattery[interaction.deliveryExecution.id];
            }
        });

        return { ...currentInteractions, finished: revertArrayOrder };
    }
</script>

<style>
    .wrapper {
        padding: 3rem 4rem;
    }

    .top-area {
        display: flex;
    }

    aside {
        margin-left: auto;
    }

    .history-back {
        cursor: pointer;
        text-decoration: underline;
        border: none;
        background-color: transparent;
        padding: 0;
        font-size: initial;
    }

    .not-started-content {
        display: grid;
        gap: 1.6rem;
    }

    :global(.rowDisabled) {
        cursor: not-allowed;
    }

    :global(.select button:disabled:hover) {
        cursor: not-allowed;
    }
    /* hide checkbox on table header cell */
    :global(thead th:nth-child(1) .cell-wrapper label) {
        visibility: hidden;
    }

    /* highlight disable line for inProgress */
    :global(.selectable-datatable-wrapper.inProgress tr:has(td[disabled])) {
        background-color: var(--color-bg-actionable-secondary-hover);
        cursor: not-allowed;
    }

    :global(.selectable-datatable-wrapper.inProgress tr:has(td[disabled]) td) {
        cursor: not-allowed;
        color: var(--color-text-disabled);
    }
</style>

<div class="wrapper">
    {#if !getHideHeader()}
        <div class="top-area">
            <header class:visually-hidden={!title}>
                <h1>
                    <span class="visually-hidden">{__('Proctoring Screen')}</span>
                    <span>{title}</span>
                </h1>
            </header>
            {#if groups.length > 0}
                <aside class:disabled={(acsEnabled && !uiControl.clickableRows) || uiControl.disableDropdown}>
                    <Dropdown
                        options={groups}
                        value={$activeGroupStore}
                        reset={false}
                        selectFocused={false}
                        disabled={uiControl.disableDropdown || selectMode}
                        on:change={handleGroupChange} />
                </aside>
            {/if}
        </div>
    {/if}
    <main aria-labelledby="header-main">
        <div class:top-area-without-dropdown={!groups.length}>
            <h2 id="header-main" class="visually-hidden">{__('Proctoring Table')}</h2>

            <SelectBarContainer
                bind:this={selectBarContainerInstance}
                rows={rows[$activeTabStore]}
                interactions={filteredInteractions[$activeTabStore]}
                on:updateRows={e => (rows[$activeTabStore] = e.detail.rows)}
                on:bulkAcs={e => bulkAcs(e.detail)}
                on:close={() => {
                    selectMode = false;
                }}
                disabled={uiControl.clickableRows === false} />

            <TabGroup {tabs} activeTab={$activeTabStore} on:change={onTabChange} />

            <div
                class="selectable-datatable-wrapper {$activeTabStore} notranslate"
                class:selectMode
                class:rowDisabled={acsEnabled && !uiControl.clickableRows}>
                {#if $activeTabStore === tabKeys.notStarted && uiControl.displayNotStartedDataTable}
                    {#key rows.notStarted}
                        {#if !rows.notStarted.length}
                            <p>
                                {__(
                                    'There may be other users who have not started the session. In order to see them, please take a look at'
                                )}
                                <button class="history-back" on:click={backToSessionOverview}
                                    >{__('the session overview page')}</button>
                            </p>
                        {:else}
                            <div class="not-started-content">
                                <DataTable
                                    {rowDisabled}
                                    {rowSelected}
                                    clickableRows={false}
                                    columns={headers.notStarted}
                                    rows={rows.notStarted}
                                    fullwidth />
                            </div>
                        {/if}
                    {/key}
                {:else if $activeTabStore === tabKeys.awaitingAuthorization}
                    {#key rows.awaitingAuthorization}
                        <DataTable
                            {rowDisabled}
                            {rowSelected}
                            clickableRows={uiControl.clickableRows && selectMode}
                            columns={headers.awaitingAuthorization}
                            rows={rows.awaitingAuthorization}
                            fullwidth
                            on:keySelect={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:sort={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:rowClick={createRowClickHandler(tabKeys.awaitingAuthorization)}
                            on:rowLongClick={createRowLongClickHandler(tabKeys.awaitingAuthorization)}
                            on:check={e => handleCheck(e.detail)}
                            on:authorize={e => handleRowAuthorize(e.detail)} />
                    {/key}
                {:else if $activeTabStore === tabKeys.inProgress}
                    {#key rows.inProgress}
                        <DataTable
                            {rowDisabled}
                            {rowSelected}
                            clickableRows={uiControl.clickableRows}
                            columns={headers.inProgress}
                            rows={rows.inProgress}
                            fullwidth
                            on:keySelect={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:sort={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:rowClick={createRowClickHandler(tabKeys.inProgress)}
                            on:rowLongClick={createRowLongClickHandler(tabKeys.inProgress)}
                            on:check={e => handleCheck(e.detail)} />
                    {/key}
                {:else if $activeTabStore === tabKeys.paused}
                    {#key rows.paused}
                        <DataTable
                            {rowDisabled}
                            {rowSelected}
                            clickableRows={uiControl.clickableRows}
                            columns={headers.paused}
                            rows={rows.paused}
                            fullwidth
                            on:keySelect={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:sort={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:rowClick={createRowClickHandler(tabKeys.paused)}
                            on:rowLongClick={createRowLongClickHandler(tabKeys.paused)}
                            on:check={e => handleCheck(e.detail)}
                            on:resume={e => handleRowResume(e.detail)} />
                    {/key}
                {:else if $activeTabStore === tabKeys.finished}
                    {#key rows.finished}
                        <DataTable
                            {rowDisabled}
                            {rowSelected}
                            clickableRows={uiControl.clickableRows}
                            columns={headers.finished}
                            rows={rows.finished}
                            fullwidth
                            on:keySelect={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:sort={e => sortInteractions(e.detail.key, e.detail.sortOrder)}
                            on:rowClick={createRowClickHandler(tabKeys.finished)}
                            on:rowLongClick={createRowLongClickHandler(tabKeys.finished)}
                            on:check={e => handleCheck(e.detail)} />
                    {/key}
                {/if}
            </div>
        </div>
    </main>
</div>
<RibbonContainer />
<div class:notification-container={selectMode}>
    <NotificationManager />
</div>
